import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { from, of } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { TOrderListActionType } from './order-list.models';
import * as OrderListActions from './order-list.actions';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class OrderListEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private router: Router,
  ) {}

  getOrderList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.GetOrderList),
      switchMap((action: OrderListActions.GetOrderList) =>
        // todo [kuldeep/jyotishka] - look into this API getting called multiple times
        from(this.appNotificationUtilClass.presentLoading('Loading', 3000)).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        const { page_size, page_number, filters } = action.payload;

        return (
          action.path !== 'ASSIGN TNA TO ORDERS SCREEN'
            ? this.plani8DataService.fetchOrderlist(action.payload)
            : this.plani8DataService.fetchOrderListingFromTna({ page_size, page_number, filters })
        ).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new OrderListActions.GetOrderListSuccess(res)),
          catchError(() => {
            return of(new OrderListActions.GetOrderListFailure());
          }),
        );
      }),
    ),
  );

  getOrderUploadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.GetOrderUploadDetails),
      switchMap((action: OrderListActions.GetOrderUploadDetails) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl]) =>
        this.plani8DataService.fetchOrderUploadDetails().pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new OrderListActions.GetOrderUploadDetailsSuccess(res)),
          catchError(() => {
            return of(new OrderListActions.GetOrderUploadDetailsFailure());
          }),
        ),
      ),
    ),
  );

  assignTnaToOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.AssignTnaToOrders),
      switchMap((action: OrderListActions.AssignTnaToOrders) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.assignTnaToOrders(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/orders/list'], { queryParams: { clearTnaToAllocate: true } });
            this.appNotificationUtilClass.presentToast('Tna allocation to order(s) is complete.');
            return new OrderListActions.AssignTnaToOrdersSuccess();
          }),
          catchError(() => {
            return of(new OrderListActions.AssignTnaToOrdersFailure());
          }),
        ),
      ),
    ),
  );

  assignSingleFactoryToOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.AssignSingleFactoryToOrder),
      switchMap((action: OrderListActions.AssignSingleFactoryToOrder) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.assignSingleFactoryToOrder(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/orders/list'], { queryParams: { clearFactoryToAllocate: true } });
            this.appNotificationUtilClass.presentToast('Factory allocation to order is complete.');
            return new OrderListActions.AssignSingleFactoryToOrderSuccess();
          }),
          catchError(() => {
            return of(new OrderListActions.AssignSingleFactoryToOrderFailure());
          }),
        ),
      ),
    ),
  );

  viewFactoryAllocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.ViewFactoryAllocation),
      switchMap((action: OrderListActions.ViewFactoryAllocation) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.viewFactoryAllocationToOrder(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            return new OrderListActions.ViewFactoryAllocationSuccess();
          }),
          catchError(() => {
            return of(new OrderListActions.ViewFactoryAllocationFailure());
          }),
        ),
      ),
    ),
  );

  getOrderToFactoriesSplit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.GetOrderToFactoriesSplit),
      switchMap((action: OrderListActions.GetOrderToFactoriesSplit) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.getOrderToFactoriesSplitView(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new OrderListActions.GetOrderToFactoriesSplitSuccess(res);
          }),
          catchError(() => {
            return of(new OrderListActions.GetOrderToFactoriesSplitFailure());
          }),
        ),
      ),
    ),
  );

  AllocateOrderToMultipleFactories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.AllocateOrderToMultipleFactories),
      switchMap((action: OrderListActions.AllocateOrderToMultipleFactories) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.allocateOrderToMultipleFactories(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/orders/list'], { queryParams: { dismissMultiAllocateModal: true } });
            this.appNotificationUtilClass.presentToast('Factories allocation to order is complete.');
            return new OrderListActions.AllocateOrderToMultipleFactoriesSuccess();
          }),
          catchError(() => {
            return of(new OrderListActions.AllocateOrderToMultipleFactoriesFailure());
          }),
        ),
      ),
    ),
  );

  deleteOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.DeleteOrders),
      switchMap((action: OrderListActions.DeleteOrders) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.deleteOrders(action.deleteOrdersPayload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new OrderListActions.GetOrderList(action.fetchOrderspayload)),
          catchError(() => {
            return of(new OrderListActions.GetOrderListFailure());
          }),
        ),
      ),
    ),
  );

  fetchViewOrderFactoryAllocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TOrderListActionType.ViewOrderFactoryAllocation),
      switchMap((action: OrderListActions.GetViewOrderFactoryAllocation) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.fetchViewOrderFactoryAllocation(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new OrderListActions.GetViewOrderFactoryAllocationSuccess(res);
          }),
          catchError(() => {
            return of(new OrderListActions.GetViewOrderFactoryAllocationFailure());
          }),
        ),
      ),
    ),
  );
}
